import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  SimpleGrid,
  Text,
  useToast,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Divider,
  Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import EmployeeDataService from "../../services/Employee";
import { getUserAccess } from "../../utils/authUtils";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";
import { hrmsDateBounds, validateProfileDates } from "./hrmsDateRules";

interface ProfileData {
  name: string;
  email: string;
  designation: string;
  dateOfJoining: string;
  birthday: string;
  address: string;
  reportingManager: string;
  nextAppraisalDueDate: string;
  role: string;
  isOnboarded: boolean;
  employeeId?: string;
  department?: string;
  division?: string;
  employmentStatus?: string;
  leaveCycleStartDate?: string;
}

const EMPLOYMENT_STATUS_LABELS: Record<string, string> = {
  probation: "Probation",
  confirmed: "Confirmed",
  notice: "Notice period",
  exited: "Exited",
};

// The next anniversary of the leave start date: when the yearly leave renews.
const nextLeaveRenewal = (leaveCycleStartDate?: string): string => {
  if (!leaveCycleStartDate) return "";
  const today = new Date().toISOString().slice(0, 10);
  if (today < leaveCycleStartDate) return leaveCycleStartDate;
  const monthDay = leaveCycleStartDate.slice(4);
  const year = Number(today.slice(0, 4));
  return `${year}${monthDay}` > today ? `${year}${monthDay}` : `${year + 1}${monthDay}`;
};

const defaultProfile: ProfileData = {
  name: "",
  email: "",
  designation: "",
  dateOfJoining: "",
  birthday: "",
  address: "",
  reportingManager: "",
  nextAppraisalDueDate: "",
  role: "employee",
  isOnboarded: false,
};

export const EmployeeProfile = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const { isAdmin } = getUserAccess();
  const toast = useToast();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [profile, setProfile] = useState<ProfileData>({
    ...defaultProfile,
    email: userEmail,
    role: isAdmin ? "admin" : "employee",
  });
  const [editedProfile, setEditedProfile] = useState<ProfileData>(profile);

  const fetchProfile = async () => {
    setIsLoading(true);
    try {
      const res = await EmployeeDataService.getEmployeeProfileByEmail(
        token,
        userEmail
      );
      console.log("fetchProfile response:", res.data);
      if (res.data.status === "success" && res.data.data) {
        const profileData = res.data.data;
        // isOnboarded: true from backend OR all onboarding fields filled
        const onboarded =
          profileData.isOnboarded === true ||
          (profileData.designation &&
            profileData.dateOfJoining &&
            profileData.birthday &&
            profileData.address);
        const data = {
          ...defaultProfile,
          ...profileData,
          isOnboarded: !!onboarded,
        };
        console.log("Profile loaded, isOnboarded:", data.isOnboarded);
        setProfile(data);
        setEditedProfile(data);
      }
    } catch (e: any) {
      console.log("fetchProfile error:", e.response?.status, e.response?.data);
      if (e.response?.status === 401) {
        toast({
          position: "top",
          title: "Authentication Failed.",
          description: "Your session is expired.",
          status: "error",
          duration: 500,
          isClosable: true,
        });
        setTimeout(() => {
          navigate("/login");
        }, 2000);
        return;
      }
      // 404 = profile not found — show onboarding
      if (e.response?.status === 404) {
        const fresh = {
          ...defaultProfile,
          email: userEmail,
          role: isAdmin ? "admin" : "employee",
        };
        setProfile(fresh);
        setEditedProfile(fresh);
      } else {
        toast({
          position: "top",
          title: "Error",
          description: "Failed to load profile.",
          status: "error",
          duration: 2000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const bounds = hrmsDateBounds();

  const handleSubmitOnboarding = async () => {
    if (
      !editedProfile.name ||
      !editedProfile.designation ||
      !editedProfile.dateOfJoining ||
      !editedProfile.birthday ||
      !editedProfile.address
    ) {
      toast({
        position: "top",
        title: "Validation Error",
        description: "Please fill all the required fields.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const dateError = validateProfileDates({ dateOfJoining: editedProfile.dateOfJoining, birthday: editedProfile.birthday });
    if (dateError) {
      toast({ position: "top", title: dateError, status: "error", duration: 3000, isClosable: true });
      return;
    }

    setIsSaving(true);
    try {
      const data = {
        email: userEmail,
        name: editedProfile.name,
        designation: editedProfile.designation,
        dateOfJoining: editedProfile.dateOfJoining,
        birthday: editedProfile.birthday,
        address: editedProfile.address,
        role: isAdmin ? "admin" : "employee",
      };

      console.log("Submitting onboarding:", data);
      const res = await EmployeeDataService.createEmployeeProfile(token, data);
      console.log("Onboarding response:", res.data);

      // If we got here (no throw), onboarding succeeded
      // Force isOnboarded: true using the submitted data as fallback
      const saved = {
        ...defaultProfile,
        ...data,
        ...(res.data.data || {}),
        isOnboarded: true,
      };
      setProfile(saved);
      setEditedProfile(saved);
      setIsEditing(false);

      toast({
        position: "top",
        title: "Success",
        description: "Onboarding completed successfully.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (e: any) {
      console.log("Onboarding error:", e.response?.status, e.response?.data);
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to save profile.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return (
    <HrmsPageShell title="My Profile" sectionKey="me" isLoading={isLoading}>
      <div className="row m-0 justify-content-center">
        <div className="col-lg-10">
            {/* ===== NOT ONBOARDED: prompt ===== */}
            {!profile.isOnboarded && !isEditing && (
              <Card boxShadow="lg" borderRadius="xl">
                <CardHeader bg="teal.500" color="white" borderTopRadius="xl">
                  <Flex justify="space-between" align="center">
                    <Heading size="md">My Profile</Heading>
                    <Badge
                      colorScheme="orange"
                      fontSize="sm"
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      NOT ONBOARDED
                    </Badge>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    py={10}
                    gap={4}
                  >
                    <Text fontSize="lg" fontWeight="bold" color="gray.600">
                      Welcome, {userEmail}!
                    </Text>
                    <Text color="gray.500" textAlign="center" maxW="md">
                      Your profile has not been set up yet. Please complete your
                      onboarding to fill in your details. This is a one-time
                      setup and your profile will be read-only after submission.
                    </Text>
                    <Button
                      colorScheme="teal"
                      size="lg"
                      mt={4}
                      onClick={() => setIsEditing(true)}
                    >
                      Start Onboarding
                    </Button>
                  </Flex>
                </CardBody>
              </Card>
            )}

            {/* ===== ONBOARDING FORM ===== */}
            {!profile.isOnboarded && isEditing && (
              <>
                <Box
                  bg="yellow.100"
                  p={4}
                  borderRadius="md"
                  mb={4}
                  borderLeft="4px solid"
                  borderLeftColor="yellow.500"
                >
                  <Text fontWeight="bold" color="yellow.800">
                    Please complete your profile information.
                  </Text>
                  <Text fontSize="sm" color="yellow.700">
                    This is a one-time setup. Your profile will be read-only
                    after submission.
                  </Text>
                </Box>

                <Card boxShadow="lg" borderRadius="xl">
                  <CardHeader bg="teal.500" color="white" borderTopRadius="xl">
                    <Flex justify="space-between" align="center">
                      <Heading size="md">Onboarding Form</Heading>
                      <Badge
                        colorScheme="orange"
                        fontSize="sm"
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        NOT ONBOARDED
                      </Badge>
                    </Flex>
                  </CardHeader>
                  <CardBody>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                      <FormControl isRequired>
                        <FormLabel>Full Name</FormLabel>
                        <Input
                          value={editedProfile.name}
                          onChange={(e) =>
                            setEditedProfile({
                              ...editedProfile,
                              name: e.target.value,
                            })
                          }
                          placeholder="Enter your full name"
                          focusBorderColor="teal.500"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Email</FormLabel>
                        <Input value={userEmail} isReadOnly bg="gray.100" />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Designation / Position</FormLabel>
                        <Input
                          value={editedProfile.designation}
                          onChange={(e) =>
                            setEditedProfile({
                              ...editedProfile,
                              designation: e.target.value,
                            })
                          }
                          placeholder="Enter your designation"
                          focusBorderColor="teal.500"
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Date of Joining</FormLabel>
                        <Input
                          type="date"
                          value={editedProfile.dateOfJoining}
                          min={bounds.joining.min}
                          max={bounds.joining.max}
                          onChange={(e) =>
                            setEditedProfile({
                              ...editedProfile,
                              dateOfJoining: e.target.value,
                            })
                          }
                          focusBorderColor="teal.500"
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Birthday</FormLabel>
                        <Input
                          type="date"
                          value={editedProfile.birthday}
                          min={bounds.birthday.min}
                          max={bounds.birthday.max}
                          onChange={(e) =>
                            setEditedProfile({
                              ...editedProfile,
                              birthday: e.target.value,
                            })
                          }
                          focusBorderColor="teal.500"
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>Address</FormLabel>
                        <Input
                          value={editedProfile.address}
                          onChange={(e) =>
                            setEditedProfile({
                              ...editedProfile,
                              address: e.target.value,
                            })
                          }
                          placeholder="Enter your address"
                          focusBorderColor="teal.500"
                        />
                      </FormControl>
                    </SimpleGrid>

                    <Flex mt={6} gap={4} justify="flex-end">
                      <Button
                        variant="outline"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        colorScheme="teal"
                        onClick={handleSubmitOnboarding}
                        isLoading={isSaving}
                      >
                        Submit Profile
                      </Button>
                    </Flex>
                  </CardBody>
                </Card>
              </>
            )}

            {/* ===== ONBOARDED: READ-ONLY PROFILE ===== */}
            {profile.isOnboarded && (
              <Card boxShadow="lg" borderRadius="xl">
                <CardHeader bg="teal.500" color="white" borderTopRadius="xl">
                  <Flex justify="space-between" align="center">
                    <Heading size="md">My Profile</Heading>
                    <Badge
                      colorScheme="green"
                      fontSize="sm"
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      ONBOARDED
                    </Badge>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Full Name
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.name || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Email
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.email || userEmail}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Designation / Position
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.designation || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Date of Joining
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.dateOfJoining || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Birthday
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.birthday || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Address
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.address || "-"}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  <Divider my={6} />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Employee ID
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.employeeId || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Department
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.department || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Division
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.division || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Employment Status
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {EMPLOYMENT_STATUS_LABELS[profile.employmentStatus || ""] || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Leave Renewal Date
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {nextLeaveRenewal(profile.leaveCycleStartDate) || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Reporting Manager
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.reportingManager || "-"}
                      </Text>
                    </Box>

                    <Box>
                      <Text
                        fontSize="sm"
                        color="gray.500"
                        fontWeight="medium"
                      >
                        Next Appraisal Due Date
                      </Text>
                      <Text fontSize="md" fontWeight="semibold">
                        {profile.nextAppraisalDueDate || "-"}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </CardBody>
              </Card>
            )}
        </div>
      </div>
    </HrmsPageShell>
  );
};
